import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  CardActions,
  Paper
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Inventory as InventoryIcon,
  MenuBook as MenuBookIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { supabase } from '../utils/supabase';

export default function UserDashboard({ session }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pantryItems: 0,
    savedRecipes: 0,
    expiringItems: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load stats when session is available
    if (session?.user) {
      loadStats();
    } else {
    }
  }, [session?.user?.id]);

  const loadStats = async () => {
    if (loading) return; // Prevent multiple calls
    
    setLoading(true);
    
    try {
      // Use the session user directly instead of making another auth call
      const user = session?.user;
      if (!user) {
        setLoading(false);
        return;
      }

      // Get pantry items count using REST API
      const pantryUrl = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients?user_id=eq.${user.id}&select=count`;
      const pantryResponse = await fetch(pantryUrl, {
        method: 'HEAD',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Prefer': 'count=exact'
        }
      });
      const pantryCount = parseInt(pantryResponse.headers.get('Content-Range')?.split('/')[1] || '0');

      // Get saved recipes count using REST API
      const recipesUrl = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipes?user_id=eq.${user.id}&select=count`;
      const recipesResponse = await fetch(recipesUrl, {
        method: 'HEAD',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Prefer': 'count=exact'
        }
      });
      const recipesCount = parseInt(recipesResponse.headers.get('Content-Range')?.split('/')[1] || '0');

      // Get expiring items (within 7 days) using REST API
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      
      const expiringUrl = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients?user_id=eq.${user.id}&expiry_date=not.is.null&expiry_date=lte.${sevenDaysFromNow.toISOString().split('T')[0]}&select=count`;
      const expiringResponse = await fetch(expiringUrl, {
        method: 'HEAD',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Prefer': 'count=exact'
        }
      });
      const expiringCount = parseInt(expiringResponse.headers.get('Content-Range')?.split('/')[1] || '0');

      const newStats = {
        pantryItems: pantryCount || 0,
        savedRecipes: recipesCount || 0,
        expiringItems: expiringCount || 0
      };

      setStats(newStats);
    } catch (error) {
      console.error('💥 Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Add Ingredients',
      description: 'Quickly add new ingredients to your pantry',
      icon: <InventoryIcon fontSize="large" />,
      action: () => navigate('/pantry'),
      color: 'primary'
    },
    {
      title: 'Generate Recipe',
      description: 'Create a recipe from your available ingredients',
      icon: <RestaurantIcon fontSize="large" />,
      action: () => navigate('/generate-recipe'),
      color: 'secondary'
    },
    {
      title: 'Browse Cookbook',
      description: 'View your saved recipes and cooking history',
      icon: <MenuBookIcon fontSize="large" />,
      action: () => navigate('/cookbook'),
      color: 'info'
    }
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Welcome Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Welcome to Virtual Kitchen
          </Typography>
          {session?.user && (
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Hello, {session.user.user_metadata?.username || session.user.email}!
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your AI-powered cooking companion. Manage your pantry, discover recipes, and reduce food waste.
          </Typography>
        </Box>

        {/* Stats Overview */}
        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          flexWrap: 'wrap',
          justifyContent: 'center',
          mb: 4
        }}>
          <Paper sx={{ 
            flex: '1 1 250px', 
            minWidth: '250px',
            maxWidth: '400px',
            p: 3, 
            textAlign: 'center', 
            bgcolor: 'primary.light', 
            color: 'primary.contrastText' 
          }}>
            <InventoryIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
              {stats.pantryItems}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Pantry Items
            </Typography>
          </Paper>
          <Paper sx={{ 
            flex: '1 1 250px', 
            minWidth: '250px',
            maxWidth: '400px',
            p: 3, 
            textAlign: 'center', 
            bgcolor: 'secondary.light', 
            color: 'secondary.contrastText' 
          }}>
            <MenuBookIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
              {stats.savedRecipes}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Saved Recipes
            </Typography>
          </Paper>
          <Paper sx={{ 
            flex: '1 1 250px', 
            minWidth: '250px',
            maxWidth: '400px',
            p: 3, 
            textAlign: 'center', 
            bgcolor: stats.expiringItems > 0 ? 'warning.light' : 'success.light',
            color: stats.expiringItems > 0 ? 'warning.contrastText' : 'success.contrastText'
          }}>
            <ScheduleIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
              {stats.expiringItems}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Expiring Soon
            </Typography>
            {stats.expiringItems > 0 && (
              <Button 
                variant="contained"
                size="small"
                sx={{ 
                  mt: 2, 
                  bgcolor: 'warning.dark',
                  '&:hover': {
                    bgcolor: 'warning.main'
                  }
                }}
                onClick={() => navigate('/pantry')}
              >
                Check Pantry
              </Button>
            )}
          </Paper>
        </Box>

        {/* Quick Actions */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
          Quick Actions
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          flexWrap: 'wrap',
          justifyContent: 'center',
          mb: 4
        }}>
          {quickActions.map((action, index) => (
            <Card key={index} sx={{ 
              flex: '1 1 280px', 
              minWidth: '280px',
              maxWidth: '400px',
              display: 'flex', 
              flexDirection: 'column',
              height: 'auto'
            }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ color: `${action.color}.main`, mb: 2 }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button 
                  variant="contained" 
                  color={action.color}
                  onClick={action.action}
                >
                  {action.title}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>

        {/* Feature Highlights */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
            What You Can Do
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <Card sx={{ 
              flex: '1 1 220px', 
              minWidth: '220px',
              maxWidth: '280px',
              height: 'auto' 
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🥬 Smart Pantry Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track your ingredients, monitor expiry dates, and get notifications before items spoil.
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ 
              flex: '1 1 220px', 
              minWidth: '220px',
              maxWidth: '280px',
              height: 'auto' 
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🤖 AI Recipe Generation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate personalized recipes based on your available ingredients and dietary preferences.
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ 
              flex: '1 1 220px', 
              minWidth: '220px',
              maxWidth: '280px',
              height: 'auto' 
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📚 Personal Cookbook
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Save your favorite recipes and build your personal collection for easy access.
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ 
              flex: '1 1 220px', 
              minWidth: '220px',
              maxWidth: '280px',
              height: 'auto' 
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ♻️ Reduce Food Waste
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get suggestions to use expiring ingredients and minimize food waste in your kitchen.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
